/**
 * Приёмка видео.
 *
 * Слева плеер, справа замечания и решение — не нужно скроллить между
 * видео и текстом. Замечание привязывается к моменту: кликнул по дорожке,
 * написал «убери эффект», монтажер получил «02:14 — убери эффект».
 * Это заменяет переписку в Telegram, ради чего система и делалась.
 *
 * Проигрывается прокси 720p. Пока она готовится, плеер отдаёт оригинал —
 * ждать транскодирование двухчасового ролика никто не должен.
 */
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Loading, Person, Row, toast } from '../components/ui';
import { bytes, duration, money, timecode } from '../lib/format';
import type { Video } from '../lib/types';

type Playback = { url: string; quality: 'proxy' | 'original'; ready: boolean };

export function ReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [position, setPosition] = useState(0);
  const [noteText, setNoteText] = useState('');
  const [pendingNotes, setPendingNotes] = useState<Array<{ text: string; timecodeSec: number }>>([]);

  const { data: video, isLoading } = useQuery({
    queryKey: ['video', id],
    queryFn: () => api.get<Video>(`/api/videos/${id}`),
    enabled: Boolean(id),
    // Пока ffmpeg не дочитал метаданные, перезапрашиваем сами.
    // Иначе человек смотрит на прочерки и не знает, что нужно обновить
    // страницу — а на длинном видео обработка идёт десятки минут.
    refetchInterval: (q) => {
      const final = q.state.data?.files?.find(
        (f) => f.kind === 'FINAL' && f.uploadStatus === 'COMPLETE',
      );
      return final && !final.durationSec ? 10_000 : false;
    },
  });

  const { data: playback } = useQuery({
    queryKey: ['playback', id],
    queryFn: () => api.get<Playback>(`/api/files/playback/${id}`),
    enabled: Boolean(id),
    // Прокси может дожариваться прямо сейчас — периодически проверяем
    refetchInterval: (q) => (q.state.data?.ready ? false : 20_000),
  });

  const accept = useMutation({
    mutationFn: () => api.post(`/api/videos/${id}/accept`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['review-queue'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast(`Принято · начислено ${money((video?.priceShoot ?? 0) + (video?.priceEdit ?? 0))}`);
      navigate('/review');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/api/videos/${id}/reject`, { notes: pendingNotes }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['review-queue'] });
      toast('Отправлено на доработку', '↺');
      navigate('/review');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const addNote = () => {
    const text = noteText.trim();
    if (!text) return toast('Напиши, что исправить', '✎');
    setPendingNotes((n) => [...n, { text, timecodeSec: Math.floor(position) }]);
    setNoteText('');
  };

  const seekTo = (sec: number) => {
    if (videoRef.current) videoRef.current.currentTime = sec;
    setPosition(sec);
  };

  if (isLoading || !video) {
    return (
      <>
        <PageHeader title="Приёмка" back="/review" />
        <Page>
          <Loading />
        </Page>
      </>
    );
  }

  // Берём именно догруженный файл: неудачные попытки загрузки оставляют
  // записи без метаданных, и без этой проверки они перекрывают настоящую
  const finalFile = video.files
    ?.filter((f) => f.kind === 'FINAL' && f.uploadStatus === 'COMPLETE')
    .sort((a, b) => (b.durationSec ?? 0) - (a.durationSec ?? 0))[0];
  const author = video.editor?.displayName ?? video.worker.displayName;
  // Замечания с прошлых кругов уже в базе, новые пока только в памяти —
  // отправятся вместе с решением «на доработку»
  const allNotes = [
    ...(video.notes?.filter((n) => !n.resolvedAt) ?? []).map((n) => ({
      key: n.id,
      text: n.text,
      timecodeSec: n.timecodeSec ?? 0,
      pendingIndex: null as number | null,
    })),
    ...pendingNotes.map((n, i) => ({
      key: `pending-${i}`,
      text: n.text,
      timecodeSec: n.timecodeSec,
      pendingIndex: i,
    })),
  ].sort((a, b) => a.timecodeSec - b.timecodeSec);

  return (
    <>
      <PageHeader
        title={video.task.title}
        sub={`ТЗ #${video.task.number} · видео ${video.index} · ${author}`}
        back="/review"
      />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-5 items-start">
          <div>
            <div className="rounded-xl2 overflow-hidden border border-line bg-black">
              {playback ? (
                <video
                  ref={videoRef}
                  src={playback.url}
                  controls
                  className="w-full aspect-video bg-black"
                  onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                />
              ) : (
                <div className="aspect-video grid place-items-center text-muted2 bg-gradient-to-br from-[#3E2C63] to-[#1E2745]">
                  Загрузка плеера…
                </div>
              )}
            </div>

            {playback && !playback.ready && (
              <div className="text-[11.5px] text-warn bg-warn/10 rounded-lg px-3 py-2 mt-3 leading-relaxed">
                Превью 720p ещё готовится — сейчас проигрывается оригинал. Приёмку это
                не блокирует, плеер переключится сам.
              </div>
            )}

            <div className="flex gap-2.5 mt-3">
              <button
                className="btn flex-1"
                onClick={() =>
                  finalFile &&
                  void api
                    .get<{ url: string }>(`/api/files/${finalFile.id}/download`)
                    .then((r) => window.open(r.url, '_blank'))
                }
                disabled={!finalFile}
              >
                ⤓ Скачать оригинал{finalFile ? ` · ${bytes(finalFile.sizeBytes)}` : ''}
              </button>
              <button
                className="btn"
                onClick={() => {
                  setPosition(videoRef.current?.currentTime ?? 0);
                  toast(`Момент ${timecode(Math.floor(position))} выбран`, '⧉');
                }}
              >
                Отметить момент
              </button>
            </div>

            <div className="text-[11.5px] text-muted2 mt-2.5 leading-relaxed">
              Скачивание идёт напрямую с сервера хранилища на полной скорости и не мешает
              работе — можно принять видео, пока файл ещё качается.
            </div>

            <div className="sec">О видео</div>
            <div className="card">
              <div className="flex justify-between items-center py-3 border-b border-[#1A1A24] first:pt-0">
                <Person id={video.worker.id} name={video.worker.displayName} sub="съёмка" size={26} />
                <b className="mono">{money(video.priceShoot)}</b>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-[#1A1A24]">
                <Person
                  id={video.editor?.id ?? video.worker.id}
                  name={video.editor?.displayName ?? video.worker.displayName}
                  sub="монтаж"
                  size={26}
                />
                <b className="mono">{money(video.priceEdit)}</b>
              </div>
              <Row label="Длительность">
                {finalFile ? duration(finalFile.durationSec) : '—'}
              </Row>
              <Row label="Разрешение">
                {finalFile?.width ? `${finalFile.width}×${finalFile.height}` : '—'}
              </Row>
              <Row label="Доработок">{video.revisionCount}</Row>
            </div>
          </div>

          <div>
            <div className="sec !mt-0">Замечание на {timecode(Math.floor(position))}</div>
            <div className="card">
              <div className="text-[11.5px] text-muted2 mb-2.5 leading-relaxed">
                Перемотай плеер на нужный момент — замечание привяжется к нему.
              </div>
              <textarea
                className="input min-h-[80px] resize-y leading-relaxed"
                placeholder="Что исправить…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote();
                }}
              />
              <button className="btn btn-sm w-full mt-2.5" onClick={addNote}>
                + Добавить замечание
              </button>
            </div>

            {allNotes.length > 0 && (
              <>
                <div className="sec">Замечания · {allNotes.length}</div>
                <div className="space-y-2">
                  {allNotes.map((n) => (
                    <div
                      key={n.key}
                      className="bg-surface2 border border-line border-l-2 border-l-warn rounded-[9px] px-3.5 py-3 text-[13.5px] leading-relaxed flex gap-2"
                    >
                      <button
                        className="text-warn mono font-bold hover:underline shrink-0"
                        onClick={() => seekTo(n.timecodeSec)}
                        title="Перемотать сюда"
                      >
                        {timecode(n.timecodeSec)}
                      </button>
                      <span className="flex-1">{n.text}</span>
                      {n.pendingIndex !== null && (
                        <button
                          className="text-muted2 hover:text-dang shrink-0"
                          onClick={() =>
                            setPendingNotes((p) => p.filter((_, j) => j !== n.pendingIndex))
                          }
                          title="Убрать"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="sec">Решение</div>
            <div className="flex gap-2.5">
              <button
                className="btn btn-warn flex-1"
                onClick={() => reject.mutate()}
                disabled={reject.isPending || accept.isPending}
              >
                На доработку
              </button>
              <button
                className="btn btn-ok flex-1"
                onClick={() => accept.mutate()}
                disabled={accept.isPending || reject.isPending}
              >
                {accept.isPending ? 'Принимаю…' : 'Принять'}
              </button>
            </div>

            <div className="text-[11.5px] text-muted2 mt-3 text-center leading-relaxed">
              При приёмке начислится {money(video.priceShoot)} → {video.worker.displayName}
              <br />и {money(video.priceEdit)} → {author}
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}
